import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import { formatMeasurement } from './measurements';

/**
 * Export annotated PDF - flattens all annotations onto the PDF
 *
 * Supports:
 * - Stamps (construction-standard)
 * - PDF Measurements (linear, area)
 * - Revision clouds
 * - Arrows
 * - Rectangles
 * - Circles/ellipses
 * - Freehand drawings
 * - Callouts
 */

// Construction-standard stamp definitions (must match annotationRenderer.js)
const STAMP_TYPES = {
  approved: { text: 'APPROVED', color: [0, 0.67, 0] },
  rejected: { text: 'REJECTED', color: [0.8, 0, 0] },
  revised: { text: 'REVISED', color: [1, 0.53, 0] },
  forReview: { text: 'FOR REVIEW', color: [0, 0.4, 1] },
  notForConstruction: { text: 'NOT FOR CONSTRUCTION', color: [0.8, 0, 0] },
  preliminary: { text: 'PRELIMINARY', color: [0.4, 0.4, 0.4] },
  asBuilt: { text: 'AS-BUILT', color: [0, 0.53, 1] },
  void: { text: 'VOID', color: [0.8, 0, 0] },
  custom: { text: 'CUSTOM', color: [0.6, 0, 1] },
};

/**
 * Parse hex color to RGB values (0-1 range)
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return [
      parseInt(result[1], 16) / 255,
      parseInt(result[2], 16) / 255,
      parseInt(result[3], 16) / 255,
    ];
  }
  return [1, 0, 0]; // Default to red
}

/**
 * Convert canvas coordinates to PDF coordinates
 * PDF coordinates: origin at bottom-left, Y increases upward
 * Canvas coordinates: origin at top-left, Y increases downward
 */
function canvasToPdf(point, pdfHeight, pdfScale, pdfPosition) {
  const x = (point.x - (pdfPosition?.x || 0)) / pdfScale;
  const y = pdfHeight - ((point.y - (pdfPosition?.y || 0)) / pdfScale);
  return { x, y };
}

/**
 * Format date for stamp display
 */
function formatStampDate(dateStr) {
  if (!dateStr) return new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

/**
 * Format area in square feet
 */
function formatArea(sqInches) {
  const sqFeet = sqInches / 144;
  if (sqFeet < 1) {
    return `${(sqFeet * 144).toFixed(0)} sq in`;
  }
  return `${sqFeet.toLocaleString(undefined, { maximumFractionDigits: 1 })} SF`;
}

/**
 * Calculate polygon centroid
 */
function getPolygonCentroid(points) {
  let cx = 0, cy = 0;
  for (const p of points) {
    cx += p.x;
    cy += p.y;
  }
  return { x: cx / points.length, y: cy / points.length };
}

/**
 * Export annotated PDF
 *
 * @param {Object} options
 * @param {ArrayBuffer} options.originalPdfBytes - Original PDF bytes
 * @param {Object} options.pdfLayer - PDF layer data (scale, position, dimensions)
 * @param {Array} options.annotations - Array of annotation objects
 * @param {Array} options.pdfMeasurements - Array of PDF measurement objects
 * @param {string} options.units - 'imperial' or 'metric'
 * @param {number} options.pageNumber - Page number to export (1-indexed), or 'all'
 * @param {string} options.mode - 'new' (create new PDF) or 'flatten' (flatten onto original)
 * @returns {Promise<Uint8Array>} - Exported PDF bytes
 */
export async function exportAnnotatedPDF(options) {
  const {
    originalPdfBytes,
    pdfLayer,
    annotations = [],
    pdfMeasurements = [],
    units = 'imperial',
    pageNumber = 1,
    mode = 'new',
  } = options;

  if (!originalPdfBytes) {
    throw new Error('No PDF data to export');
  }

  // Load the original PDF
  const pdfDoc = await PDFDocument.load(originalPdfBytes);
  const pages = pdfDoc.getPages();

  // Get font for text
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const monoFont = await pdfDoc.embedFont(StandardFonts.Courier);
  const monoBoldFont = await pdfDoc.embedFont(StandardFonts.CourierBold);

  // Determine which pages to process
  const pagesToProcess = pageNumber === 'all'
    ? pages.map((_, i) => i + 1)
    : [pageNumber];

  // Calculate scale factor from canvas to PDF coordinates
  // The pdfLayer.scale is the real-world scale (e.g., 1" = 20')
  // We need to convert from canvas pixels to PDF points
  const FLOOR_PLAN_PX_PER_INCH = 40 / 12; // ~3.33 pixels per inch in floor plan
  const PDF_DPI = 72; // PDF points per inch

  // pdfLayer.scale represents real-world inches per PDF inch
  // Combined with the render scale used when importing
  const renderScale = pdfLayer.renderScale || 2;
  const pdfScale = pdfLayer.scale
    ? (pdfLayer.scale * FLOOR_PLAN_PX_PER_INCH) / PDF_DPI
    : 1;

  for (const pageNum of pagesToProcess) {
    const pageIndex = pageNum - 1;
    if (pageIndex < 0 || pageIndex >= pages.length) continue;

    const page = pages[pageIndex];
    const { width: pdfWidth, height: pdfHeight } = page.getSize();

    // Helper to convert coordinates
    const toPdf = (point) => canvasToPdf(point, pdfHeight, pdfScale, pdfLayer?.position);

    // Only draw annotations for the current page (if we have page-specific data)
    // For now, draw all annotations on the specified page

    // Draw PDF Measurements
    for (const measurement of pdfMeasurements) {
      if (measurement.type === 'linear') {
        await drawLinearMeasurement(page, measurement, toPdf, pdfHeight, font, boldFont, units);
      } else if (measurement.type === 'area') {
        await drawAreaMeasurement(page, measurement, toPdf, pdfHeight, font, boldFont);
      }
    }

    // Draw Annotations
    for (const annotation of annotations) {
      switch (annotation.type) {
        case 'stamp':
          await drawStamp(page, annotation, toPdf, pdfHeight, monoBoldFont, monoFont);
          break;
        case 'arrow':
          await drawArrow(page, annotation, toPdf, pdfHeight);
          break;
        case 'rectangle':
          await drawRectangle(page, annotation, toPdf, pdfHeight);
          break;
        case 'circle':
          await drawCircle(page, annotation, toPdf, pdfHeight);
          break;
        case 'freehand':
          await drawFreehand(page, annotation, toPdf, pdfHeight);
          break;
        case 'cloud':
          await drawCloud(page, annotation, toPdf, pdfHeight, boldFont);
          break;
        case 'callout':
          await drawCallout(page, annotation, toPdf, pdfHeight, boldFont, font);
          break;
        default:
          break;
      }
    }
  }

  // Save and return the PDF bytes
  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
}

/**
 * Draw a linear measurement on the PDF
 */
async function drawLinearMeasurement(page, measurement, toPdf, pdfHeight, font, boldFont, units) {
  const { points, length } = measurement;
  if (!points || points.length < 2) return;

  const p1 = toPdf(points[0]);
  const p2 = toPdf(points[1]);

  const color = rgb(0, 0.78, 1); // Cyan blue
  const lineWidth = 1.5;

  // Draw main line
  page.drawLine({
    start: { x: p1.x, y: p1.y },
    end: { x: p2.x, y: p2.y },
    thickness: lineWidth,
    color,
  });

  // Draw end markers (small circles)
  const markerRadius = 3;
  page.drawCircle({
    x: p1.x,
    y: p1.y,
    size: markerRadius,
    color,
  });
  page.drawCircle({
    x: p2.x,
    y: p2.y,
    size: markerRadius,
    color,
  });

  // Calculate midpoint and angle for label
  const midX = (p1.x + p2.x) / 2;
  const midY = (p1.y + p2.y) / 2;
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  let angle = Math.atan2(dy, dx) * (180 / Math.PI);

  // Keep text readable
  if (angle > 90 || angle < -90) {
    angle += 180;
  }

  // Format the measurement text
  const text = formatMeasurement(length / 12, units);
  const fontSize = 9;
  const textWidth = boldFont.widthOfTextAtSize(text, fontSize);

  // Draw label background
  const bgPadding = 4;
  const bgWidth = textWidth + bgPadding * 2;
  const bgHeight = fontSize + bgPadding;

  // Background rectangle (rotated)
  page.drawRectangle({
    x: midX - bgWidth / 2,
    y: midY - bgHeight / 2,
    width: bgWidth,
    height: bgHeight,
    color: rgb(0.05, 0.07, 0.09),
    borderColor: color,
    borderWidth: 0.5,
    rotate: degrees(angle),
  });

  // Draw label text
  page.drawText(text, {
    x: midX - textWidth / 2,
    y: midY - fontSize / 3,
    size: fontSize,
    font: boldFont,
    color,
    rotate: degrees(angle),
  });
}

/**
 * Draw an area measurement on the PDF
 */
async function drawAreaMeasurement(page, measurement, toPdf, pdfHeight, font, boldFont) {
  const { points, area } = measurement;
  if (!points || points.length < 3) return;

  const pdfPoints = points.map(toPdf);
  const color = rgb(0, 1, 0.67); // Green
  const lineWidth = 1.5;

  // Draw polygon outline
  for (let i = 0; i < pdfPoints.length; i++) {
    const p1 = pdfPoints[i];
    const p2 = pdfPoints[(i + 1) % pdfPoints.length];
    page.drawLine({
      start: { x: p1.x, y: p1.y },
      end: { x: p2.x, y: p2.y },
      thickness: lineWidth,
      color,
    });
  }

  // Draw vertex markers
  for (const p of pdfPoints) {
    page.drawCircle({
      x: p.x,
      y: p.y,
      size: 3,
      color,
    });
  }

  // Calculate centroid for label
  const centroid = getPolygonCentroid(pdfPoints);

  // Format area text
  const text = formatArea(area);
  const fontSize = 10;
  const textWidth = boldFont.widthOfTextAtSize(text, fontSize);

  // Draw label background
  const bgPadding = 5;
  const bgWidth = textWidth + bgPadding * 2;
  const bgHeight = fontSize + bgPadding * 2;

  page.drawRectangle({
    x: centroid.x - bgWidth / 2,
    y: centroid.y - bgHeight / 2,
    width: bgWidth,
    height: bgHeight,
    color: rgb(0.05, 0.07, 0.09),
    borderColor: color,
    borderWidth: 0.5,
  });

  // Draw label text
  page.drawText(text, {
    x: centroid.x - textWidth / 2,
    y: centroid.y - fontSize / 3,
    size: fontSize,
    font: boldFont,
    color,
  });
}

/**
 * Draw a stamp annotation on the PDF
 */
async function drawStamp(page, annotation, toPdf, pdfHeight, boldFont, regularFont) {
  const { position, stampType = 'approved', customText, date, stampScale = 1 } = annotation;
  if (!position) return;

  const pos = toPdf(position);
  const stamp = STAMP_TYPES[stampType] || STAMP_TYPES.approved;
  const displayText = stamp.text === 'CUSTOM' && customText ? customText : stamp.text;
  const displayDate = formatStampDate(date);

  const [r, g, b] = stamp.color;
  const color = rgb(r, g, b);

  const fontSize = 14 * stampScale;
  const dateFontSize = 8 * stampScale;
  const padding = 8 * stampScale;
  const borderWidth = 2 * stampScale;

  // Measure text
  const textWidth = boldFont.widthOfTextAtSize(displayText, fontSize);
  const dateWidth = regularFont.widthOfTextAtSize(displayDate, dateFontSize);
  const contentWidth = Math.max(textWidth, dateWidth);
  const boxWidth = contentWidth + padding * 2;
  const boxHeight = fontSize + dateFontSize + padding * 2.5;

  // Draw outer border
  page.drawRectangle({
    x: pos.x - boxWidth / 2,
    y: pos.y - boxHeight / 2,
    width: boxWidth,
    height: boxHeight,
    borderColor: color,
    borderWidth,
    color: rgb(r, g, b, 0.15),
  });

  // Draw inner border
  const innerOffset = borderWidth + 2 * stampScale;
  page.drawRectangle({
    x: pos.x - boxWidth / 2 + innerOffset,
    y: pos.y - boxHeight / 2 + innerOffset,
    width: boxWidth - innerOffset * 2,
    height: boxHeight - innerOffset * 2,
    borderColor: color,
    borderWidth: 0.5,
  });

  // Draw main text
  page.drawText(displayText, {
    x: pos.x - textWidth / 2,
    y: pos.y + dateFontSize / 2,
    size: fontSize,
    font: boldFont,
    color,
  });

  // Draw date
  page.drawText(displayDate, {
    x: pos.x - dateWidth / 2,
    y: pos.y - fontSize / 2 - 2 * stampScale,
    size: dateFontSize,
    font: regularFont,
    color,
  });
}

/**
 * Draw an arrow annotation on the PDF
 */
async function drawArrow(page, annotation, toPdf, pdfHeight) {
  const { start, end, color: hexColor = '#ff0000', thickness = 3 } = annotation;
  if (!start || !end) return;

  const p1 = toPdf(start);
  const p2 = toPdf(end);
  const [r, g, b] = hexToRgb(hexColor);
  const color = rgb(r, g, b);

  // Draw main line
  page.drawLine({
    start: { x: p1.x, y: p1.y },
    end: { x: p2.x, y: p2.y },
    thickness,
    color,
  });

  // Draw arrowhead
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const angle = Math.atan2(dy, dx);
  const length = Math.sqrt(dx * dx + dy * dy);
  const headLength = Math.min(15, length * 0.3);
  const headAngle = Math.PI / 6;

  const head1x = p2.x - headLength * Math.cos(angle - headAngle);
  const head1y = p2.y - headLength * Math.sin(angle - headAngle);
  const head2x = p2.x - headLength * Math.cos(angle + headAngle);
  const head2y = p2.y - headLength * Math.sin(angle + headAngle);

  page.drawLine({
    start: { x: p2.x, y: p2.y },
    end: { x: head1x, y: head1y },
    thickness,
    color,
  });
  page.drawLine({
    start: { x: p2.x, y: p2.y },
    end: { x: head2x, y: head2y },
    thickness,
    color,
  });
}

/**
 * Draw a rectangle annotation on the PDF
 */
async function drawRectangle(page, annotation, toPdf, pdfHeight) {
  const { start, end, color: hexColor = '#ff0000', thickness = 3, filled = false } = annotation;
  if (!start || !end) return;

  const p1 = toPdf(start);
  const p2 = toPdf(end);
  const [r, g, b] = hexToRgb(hexColor);
  const color = rgb(r, g, b);

  const x = Math.min(p1.x, p2.x);
  const y = Math.min(p1.y, p2.y);
  const width = Math.abs(p2.x - p1.x);
  const height = Math.abs(p2.y - p1.y);

  page.drawRectangle({
    x,
    y,
    width,
    height,
    borderColor: color,
    borderWidth: thickness,
    color: filled ? rgb(r, g, b, 0.2) : rgb(r, g, b, 0.05),
  });
}

/**
 * Draw a circle/ellipse annotation on the PDF
 */
async function drawCircle(page, annotation, toPdf, pdfHeight) {
  const { center, radiusX, radiusY, color: hexColor = '#ff0000', thickness = 3 } = annotation;
  if (!center) return;

  const pos = toPdf(center);
  const [r, g, b] = hexToRgb(hexColor);
  const color = rgb(r, g, b);

  // pdf-lib doesn't have native ellipse support, so we draw a circle
  // For true ellipses, we would need to use custom path operations
  const rx = (radiusX || 50) / (annotation.pdfScale || 1);
  const ry = (radiusY || rx) / (annotation.pdfScale || 1);

  // Draw as circle using average radius (simplified)
  const avgRadius = (rx + ry) / 2;

  page.drawEllipse({
    x: pos.x,
    y: pos.y,
    xScale: rx,
    yScale: ry,
    borderColor: color,
    borderWidth: thickness,
    color: rgb(r, g, b, 0.05),
  });
}

/**
 * Draw a freehand annotation on the PDF
 */
async function drawFreehand(page, annotation, toPdf, pdfHeight) {
  const { points, color: hexColor = '#ff0000', thickness = 3 } = annotation;
  if (!points || points.length < 2) return;

  const pdfPoints = points.map(toPdf);
  const [r, g, b] = hexToRgb(hexColor);
  const color = rgb(r, g, b);

  // Draw lines between consecutive points
  for (let i = 1; i < pdfPoints.length; i++) {
    page.drawLine({
      start: { x: pdfPoints[i - 1].x, y: pdfPoints[i - 1].y },
      end: { x: pdfPoints[i].x, y: pdfPoints[i].y },
      thickness,
      color,
    });
  }
}

/**
 * Draw a revision cloud annotation on the PDF
 */
async function drawCloud(page, annotation, toPdf, pdfHeight, boldFont) {
  const { points, start, end, color: hexColor = '#ff0000', thickness = 2, label = '' } = annotation;

  let cloudPoints;

  if (points && points.length >= 3) {
    cloudPoints = points.map(toPdf);
  } else if (start && end) {
    // Legacy rectangle-based cloud
    const p1 = toPdf(start);
    const p2 = toPdf(end);
    const x = Math.min(p1.x, p2.x);
    const y = Math.min(p1.y, p2.y);
    const width = Math.abs(p2.x - p1.x);
    const height = Math.abs(p2.y - p1.y);

    cloudPoints = [
      { x, y },
      { x: x + width, y },
      { x: x + width, y: y + height },
      { x, y: y + height },
    ];
  } else {
    return;
  }

  const [r, g, b] = hexToRgb(hexColor);
  const color = rgb(r, g, b);

  // Draw cloud bumpy edges between points
  const bumpSize = 8;

  for (let i = 0; i < cloudPoints.length; i++) {
    const p1 = cloudPoints[i];
    const p2 = cloudPoints[(i + 1) % cloudPoints.length];

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const numBumps = Math.max(3, Math.floor(dist / (bumpSize * 2)));

    // Draw bumpy segments (simplified - just draw arcs)
    for (let j = 0; j < numBumps; j++) {
      const t1 = j / numBumps;
      const t2 = (j + 1) / numBumps;

      const x1 = p1.x + dx * t1;
      const y1 = p1.y + dy * t1;
      const x2 = p1.x + dx * t2;
      const y2 = p1.y + dy * t2;

      // For PDF, we just draw lines (true curved clouds would need SVG path)
      page.drawLine({
        start: { x: x1, y: y1 },
        end: { x: x2, y: y2 },
        thickness,
        color,
      });
    }
  }

  // Draw label if present
  if (label) {
    const centroid = getPolygonCentroid(cloudPoints);
    const fontSize = 10;
    const textWidth = boldFont.widthOfTextAtSize(label, fontSize);

    // Label background
    const bgPadding = 4;
    const bgWidth = textWidth + bgPadding * 2;
    const bgHeight = fontSize + bgPadding * 2;

    page.drawRectangle({
      x: centroid.x - bgWidth / 2,
      y: centroid.y - bgHeight / 2,
      width: bgWidth,
      height: bgHeight,
      color: rgb(1, 1, 1),
      borderColor: color,
      borderWidth: 1,
    });

    page.drawText(label, {
      x: centroid.x - textWidth / 2,
      y: centroid.y - fontSize / 3,
      size: fontSize,
      font: boldFont,
      color,
    });
  }
}

/**
 * Draw a callout annotation on the PDF
 */
async function drawCallout(page, annotation, toPdf, pdfHeight, boldFont, regularFont) {
  const { position, number = 1, noteText = '' } = annotation;
  if (!position) return;

  const pos = toPdf(position);
  const radius = 10;

  // Draw circle
  page.drawCircle({
    x: pos.x,
    y: pos.y,
    size: radius,
    color: rgb(1, 0, 0),
    borderColor: rgb(1, 1, 1),
    borderWidth: 1.5,
  });

  // Draw number
  const text = String(number);
  const fontSize = 10;
  const textWidth = boldFont.widthOfTextAtSize(text, fontSize);

  page.drawText(text, {
    x: pos.x - textWidth / 2,
    y: pos.y - fontSize / 3,
    size: fontSize,
    font: boldFont,
    color: rgb(1, 1, 1),
  });

  // Draw note preview if exists
  if (noteText) {
    const preview = noteText.substring(0, 30) + (noteText.length > 30 ? '...' : '');
    const previewFontSize = 8;
    const previewWidth = regularFont.widthOfTextAtSize(preview, previewFontSize);

    // Note bubble
    const bubbleX = pos.x + radius + 6;
    const bubbleY = pos.y - 8;

    page.drawRectangle({
      x: bubbleX,
      y: bubbleY,
      width: previewWidth + 8,
      height: 16,
      color: rgb(1, 1, 1),
      borderColor: rgb(1, 0, 0),
      borderWidth: 0.5,
    });

    page.drawText(preview, {
      x: bubbleX + 4,
      y: bubbleY + 4,
      size: previewFontSize,
      font: regularFont,
      color: rgb(0.2, 0.2, 0.2),
    });
  }
}

/**
 * Download PDF as file
 */
export function downloadPDF(pdfBytes, filename = 'annotated-plan.pdf') {
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default { exportAnnotatedPDF, downloadPDF };
